import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { TimeContext } from '../TimeContext';
import './Homepage.css';
import './CampusLife.css';
import { SiteHeader, SiteFooter } from './Homepage';

const CampusLife = () => {
  const { t, i18n } = useTranslation();
  const currentTime = useContext(TimeContext);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isSlideshowPaused, setIsSlideshowPaused] = useState(false);

  const navLinks = [
    { to:'/',            label:t('nav.home','Home'),        ariaLabel:t('nav.home','Home') },
    { to:'/programs',    label:t('nav.programs','Programs'),ariaLabel:t('nav.programs','Programs') },
    { to:'/campus-life', label:t('nav.campusLife','Campus Life'), ariaLabel:t('nav.campusLife','Campus Life') },
    { to:'/alumni',      label:t('nav.alumni','Alumni'),    ariaLabel:t('nav.alumni','Alumni') },
    { to:'/enrollment',  label:t('nav.enrollment','Enrollment'), ariaLabel:t('nav.enrollment','Enrollment') },
  ];

  const campusSlides = [
    {
      image:'/Campus-life/slide1.jpg',
      title:t('campusLife.slide1.title','Vibrant Student Community'),
      description:t('campusLife.slide1.description','Join our diverse student body in engaging cultural and academic events.'),
      alt:t('campusLife.slide1.alt','Students participating in a cultural event at San Tomas Tourism College'),
    },
    {
      image:'/Campus-life/slide2.jpg',
      title:t('campusLife.slide2.title','Modern Learning Facilities'),
      description:t('campusLife.slide2.description','Explore our state-of-the-art classrooms and language labs.'),
      alt:t('campusLife.slide2.alt','Modern classroom with students at San Tomas Tourism College'),
    },
    {
      image:'/Campus-life/slide3.jpg',
      title:t('campusLife.slide3.title','Extracurricular Activities'),
      description:t('campusLife.slide3.description','Participate in sports, clubs, and community outreach programs.'),
      alt:t('campusLife.slide3.alt','Students playing sports at San Tomas Tourism College'),
    },
    {
      image:'/Campus-life/slide4.jpg',
      title:t('campusLife.slide4.title','Nairobi City Experience'),
      description:t('campusLife.slide4.description','Enjoy the vibrant city life of Nairobi, right at our doorstep.'),
      alt:t('campusLife.slide4.alt','Students exploring Nairobi city near San Tomas Tourism College'),
    },
  ];

  useEffect(() => {
    if (isSlideshowPaused || campusSlides.length <= 1) return;
    const slideInterval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % campusSlides.length);
    }, 6000);
    return () => clearInterval(slideInterval);
  }, [isSlideshowPaused, campusSlides.length]);

  const handleSlideInteraction = () => {
    setIsSlideshowPaused(true);
    setTimeout(() => setIsSlideshowPaused(false), 30000);
  };

  const handlePrevSlide = () => {
    handleSlideInteraction();
    setActiveSlide(prev => (prev - 1 + campusSlides.length) % campusSlides.length);
  };
  const handleNextSlide = () => {
    handleSlideInteraction();
    setActiveSlide(prev => (prev + 1) % campusSlides.length);
  };

  const campusHighlights = [
    { emoji:'📚', title:'Modern Library', desc:'Thousands of tourism, hospitality, and language resources — print and digital.' },
    { emoji:'🗣️', title:'Language Labs', desc:'State-of-the-art audio-visual labs for immersive language learning in 10+ languages.' },
    { emoji:'🍽️', title:'Hospitality Lab', desc:'Full-service practice kitchen and restaurant for hands-on hospitality training.' },
    { emoji:'💻', title:'Computer Suite', desc:'Up-to-date IT facilities with industry-standard software and high-speed internet.' },
    { emoji:'🎭', title:'Cultural Events', desc:'Regular cultural showcases, debate competitions, and student leadership programs.' },
    { emoji:'🌆', title:'Nairobi Location', desc:'Centrally located in Nairobi CBD, putting you at the heart of Kenya\'s capital.' },
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{t('campusLife.pageTitle','Campus Life - San Tomas Tourism College')}</title>
        <meta name="description" content="Experience the vibrant campus life at San Tomas Tourism College in Nairobi." />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap" rel="stylesheet" />
        <link rel="canonical" href="https://santomastourismcollege.com/campus-life" />
      </Helmet>

      <a href="#main-content" className="sr-only focus:not-sr-only">{t('a11y.skipToContent','Skip to main content')}</a>

      <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />

      {/* ── HERO ── */}
      <section className="sttc-hero" style={{ minHeight:'42vh' }}>
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-geo" aria-hidden="true" />
        <div className="sttc-container" style={{ position:'relative', zIndex:2, paddingTop:128, paddingBottom:60 }}>
          <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}>
            <div className="section-eyebrow" style={{ color:'rgba(251,191,36,0.9)' }}>Information House, Nairobi CBD</div>
            <h1 className="section-title" style={{ color:'#fff', fontSize:'clamp(38px,5vw,62px)' }}>
              Campus <em style={{ color:'#FBBF24' }}>Life</em>
            </h1>
            <p style={{ fontSize:16, color:'rgba(255,255,255,0.78)', maxWidth:520, lineHeight:1.8, fontWeight:300, marginTop:14 }}>
              A vibrant, inspiring environment in the heart of Nairobi — where learning meets life, culture, and community.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── CAROUSEL ── */}
      <section
        id="main-content"
        className="sttc-section white"
        aria-roledescription="carousel"
        aria-label={t('a11y.campusSlideshow','Campus life slideshow')}
      >
        <div className="sttc-container">
          <div className="text-center mb-8">
            <div className="section-eyebrow">Gallery</div>
            <h2 className="section-title">Life at <em>San Tomas</em></h2>
          </div>

          <div style={{ position:'relative' }}
            onMouseEnter={handleSlideInteraction}
            onTouchStart={handleSlideInteraction}
          >
            {/* Slide */}
            <div className="campus-slide" style={{ borderRadius:16, overflow:'hidden', position:'relative', aspectRatio:'16/7', minHeight:320, background:'#1E3A8A' }}>
              <AnimatePresence mode="wait">
                {campusSlides.map((slide, idx) => (
                  activeSlide === idx && (
                    <motion.div
                      key={idx}
                      style={{ position:'absolute', inset:0 }}
                      initial={{ opacity:0 }}
                      animate={{ opacity:1 }}
                      exit={{ opacity:0 }}
                      transition={{ duration:1 }}
                      role="group" aria-roledescription="slide"
                      aria-label={`${idx+1} of ${campusSlides.length}: ${slide.title}`}
                    >
                      <img
                        src={slide.image}
                        alt={slide.alt}
                        style={{ width:'100%', height:'100%', objectFit:'cover' }}
                        loading={idx===0?'eager':'lazy'}
                        decoding="async"
                        onError={e=>{ e.target.style.opacity='0'; }}
                      />
                      <div className="campus-slide-overlay" />
                      <div className="campus-slide-content">
                        <motion.h3 initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.3 }}>
                          {slide.title}
                        </motion.h3>
                        <motion.p initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.45 }}>
                          {slide.description}
                        </motion.p>
                      </div>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>

              {/* Nav arrows */}
              <div style={{ position:'absolute', top:'50%', left:16, transform:'translateY(-50%)', zIndex:5 }}>
                <button
                  className="carousel-nav-button"
                  onClick={handlePrevSlide}
                  aria-label={t('a11y.prevSlide','Previous slide')}
                >
                  <FaArrowLeft />
                </button>
              </div>
              <div style={{ position:'absolute', top:'50%', right:16, transform:'translateY(-50%)', zIndex:5 }}>
                <button
                  className="carousel-nav-button"
                  onClick={handleNextSlide}
                  aria-label={t('a11y.nextSlide','Next slide')}
                >
                  <FaArrowRight />
                </button>
              </div>

              {/* Dots */}
              <div style={{ position:'absolute', bottom:20, left:'50%', transform:'translateX(-50%)', display:'flex', gap:8, zIndex:5 }}>
                {campusSlides.map((_,idx) => (
                  <button
                    key={idx}
                    onClick={() => { setActiveSlide(idx); handleSlideInteraction(); }}
                    style={{ width: activeSlide===idx ? 28 : 8, height:8, borderRadius:4, background: activeSlide===idx ? '#FBBF24' : 'rgba(255,255,255,0.45)', border:'none', cursor:'pointer', transition:'all .4s', padding:0 }}
                    aria-label={`Go to slide ${idx+1}`}
                    aria-current={activeSlide===idx}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── HIGHLIGHTS ── */}
      <section className="sttc-section tinted">
        <div className="sttc-container">
          <div className="text-center mb-8">
            <div className="section-eyebrow">Facilities & Activities</div>
            <h2 className="section-title">Campus <em>Highlights</em></h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:22 }}>
            {campusHighlights.map((item, i) => (
              <motion.div
                key={i}
                className="purpose-card"
                style={{ '--card-color': '#4169E1' }}
                initial={{ opacity:0, y:36 }} whileInView={{ opacity:1, y:0 }}
                viewport={{ once:true }} transition={{ duration:0.5, delay:i*0.07 }}
                whileHover={{ scale:1.02 }}
                tabIndex="0"
              >
                <div style={{ fontSize:32, marginBottom:16 }}>{item.emoji}</div>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:19, color:'#1E3A8A', marginBottom:10, fontWeight:900 }}>{item.title}</h3>
                <p style={{ fontSize:13, color:'#6B7280', lineHeight:1.75 }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="sttc-stripe" />

      {/* ── CTA ── */}
      <section className="sttc-section faculty-section-bg">
        <div className="sttc-container">
          <div style={{ maxWidth:640, margin:'0 auto', textAlign:'center' }}>
            <div className="section-eyebrow" style={{ justifyContent:'center' }}>Join Us</div>
            <h2 className="section-title" style={{ color:'#fff', marginBottom:20 }}>
              Experience Campus Life <em>First-Hand</em>
            </h2>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.72)', lineHeight:1.8, marginBottom:36 }}>
              Book an open day visit or apply to become part of the San Tomas family. We'd love to show you around.
            </p>
            <div style={{ display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap' }}>
              <Link to="/enrollment" className="btn-primary">Apply Now <FaArrowRight /></Link>
              <a href="mailto:info@santomastourismcollege.com" className="btn-secondary">Book a Visit</a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter t={t} currentTime={currentTime} />
    </div>
  );
};

export default CampusLife;